import { CheckoutEventNames, initializePaddle, Paddle } from '@paddle/paddle-js';
import { apiEndpoints } from '../utils/api';

/**
 * Payment-provider-aware checkout.
 *
 * The active provider is decided by the backend (PAYMENT_PROVIDER) and surfaced via
 * GET /billing/payment-config, so the UI switches from the CityPay paylink redirect to
 * the Paddle.js overlay with no frontend redeploy. `startCheckout` handles the buy flow;
 * `ensurePaddleInitialized` + `openTransactionCheckout` support Paddle-initiated links
 * (invoice payments, dunning, update-payment-method) that arrive as `?_ptxn=<txn>`.
 */

export interface PaymentConfig {
  provider: string;
  paddle?: { client_token: string; environment: string };
}

interface PaddleCheckoutParams {
  items?: { price_id: string; quantity: number }[];
  transaction_id?: string;
  custom_data?: Record<string, any>;
  customer_id?: string;
}

export interface CheckoutResponse {
  payment_url?: string | null;
  checkout?: PaddleCheckoutParams | null;
}

interface ApiLike {
  get: <T = any>(url: string) => Promise<T>;
}

const SUCCESS_SCREEN_MS = 2000;

let paddlePromise: Promise<Paddle | undefined> | null = null;
// The event callback is registered once at init, so route "completed" to the latest caller
// (a Subscribe click); Paddle-initiated (_ptxn) checkouts have no caller and clean the URL.
let onCompleteRef: (() => void) | undefined;

function removePtxnParam(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.has('_ptxn')) {
    url.searchParams.delete('_ptxn');
    window.history.replaceState({}, '', url.toString());
  }
}

/**
 * Initialize Paddle.js once (idempotent) from the backend payment config. Returns the
 * Paddle instance, or undefined if the active provider isn't Paddle. Safe to call at app
 * bootstrap and again on each checkout — the underlying SDK init happens at most once.
 */
export async function ensurePaddleInitialized(api: ApiLike): Promise<Paddle | undefined> {
  if (paddlePromise) return paddlePromise;

  const config = await api.get<PaymentConfig>(apiEndpoints.billing.paymentConfig);
  if (config.provider !== 'paddle' || !config.paddle?.client_token) return undefined;

  paddlePromise = initializePaddle({
    token: config.paddle.client_token,
    environment: config.paddle.environment as 'sandbox' | 'production',
    eventCallback: (event) => {
      if (event?.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
        // Paddle leaves its success screen open; show it briefly, then hand the page back.
        setTimeout(() => paddlePromise?.then((paddle) => paddle?.Checkout.close()), SUCCESS_SCREEN_MS);
        if (onCompleteRef) {
          onCompleteRef();
        } else {
          removePtxnParam();
        }
      }
    },
  });
  return paddlePromise;
}

// Match the app's light/dark mode (DarkModeContext toggles the `dark` class on <html>).
function overlaySettings(): { displayMode: 'overlay'; theme: 'light' | 'dark' } {
  return { displayMode: 'overlay', theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light' };
}

/**
 * Open the overlay for a pre-created Paddle transaction id (from a `?_ptxn=` link) and
 * strip the param so a refresh/back doesn't reopen it.
 */
export function openTransactionCheckout(paddle: Paddle, transactionId: string): void {
  paddle.Checkout.open({ transactionId, settings: overlaySettings() });
  removePtxnParam();
}

/**
 * Start checkout for a billing response.
 * - CityPay: redirects to the returned paylink URL.
 * - Paddle: opens the Paddle.js overlay; `onComplete` fires on checkout.completed.
 */
export async function startCheckout(
  api: ApiLike,
  response: CheckoutResponse,
  opts?: { onComplete?: () => void },
): Promise<void> {
  // CityPay (or any redirect provider).
  if (response.payment_url) {
    window.location.href = response.payment_url;
    return;
  }

  // Paddle overlay.
  if (response.checkout) {
    onCompleteRef = opts?.onComplete;
    const paddle = await ensurePaddleInitialized(api);
    if (!paddle) {
      throw new Error('Checkout is unavailable right now. Please try again later.');
    }

    const c = response.checkout;
    paddle.Checkout.open({
      ...(c.transaction_id
        ? { transactionId: c.transaction_id }
        : { items: (c.items || []).map((i) => ({ priceId: i.price_id, quantity: i.quantity })) }),
      ...(c.customer_id ? { customer: { id: c.customer_id } } : {}),
      customData: c.custom_data,
      settings: overlaySettings(),
    });
    return;
  }

  throw new Error('Checkout could not be started.');
}
